/**
 * Messaging API client
 * Uses the existing http (axios) instance with JWT interceptor
 */

import http from '@/network/http';
import type { Conversation, ChatMessage, Channel, UserPresence } from './types';

export const messagingApi = {
  // Conversations
  getConversations: (params?: { limit?: number; offset?: number; archived?: boolean }) =>
    http.get<{ conversations: Conversation[] }>('/messaging/conversations', { params })
      .then(r => r.data),

  createConversation: (data: {
    type?: 'dm' | 'group';
    name?: string;
    description?: string;
    participantIds: string[];
  }) =>
    http.post<{ conversation: Conversation; existing: boolean }>('/messaging/conversations', data)
      .then(r => r.data),

  getConversation: (id: string) =>
    http.get<Conversation>(`/messaging/conversations/${id}`)
      .then(r => r.data),

  updateConversation: (id: string, data: { name?: string; description?: string; avatar?: string }) =>
    http.patch<Conversation>(`/messaging/conversations/${id}`, data)
      .then(r => r.data),

  leaveConversation: (id: string) =>
    http.delete(`/messaging/conversations/${id}`)
      .then(r => r.data),

  deleteConversation: (id: string) =>
    http.delete(`/messaging/conversations/${id}/delete`)
      .then(r => r.data),

  // Participants
  addParticipants: (conversationId: string, participantIds: string[]) =>
    http.post(`/messaging/conversations/${conversationId}/participants`, { participantIds })
      .then(r => r.data),

  removeParticipant: (conversationId: string, userId: string) =>
    http.delete(`/messaging/conversations/${conversationId}/participants/${userId}`)
      .then(r => r.data),

  updateParticipant: (conversationId: string, data: {
    userId?: string;
    role?: string;
    isMuted?: boolean;
    isArchived?: boolean;
    mutedUntil?: string | null;
    markedUnreadAt?: string | null;
  }) =>
    http.patch(`/messaging/conversations/${conversationId}/participants`, data)
      .then(r => r.data),

  // Messages
  getMessages: (conversationId: string, params?: { beforeSequenceId?: number; limit?: number }) =>
    http.get<{
      messages: ChatMessage[];
      hasMore: boolean;
      oldestSequenceId: number | null;
    }>(`/messaging/conversations/${conversationId}/messages`, { params })
      .then(r => r.data),

  searchMessages: (conversationId: string, data: {
    q: string;
    sender?: string;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  }) =>
    http.post<{ messages: ChatMessage[]; total: number }>(
      `/messaging/conversations/${conversationId}/messages/search`,
      data
    ).then(r => r.data),

  getStarredMessages: (conversationId: string) =>
    http.get<{ messages: ChatMessage[] }>(
      `/messaging/conversations/${conversationId}/messages/starred`
    ).then(r => r.data),

  getGlobalStarredMessages: () =>
    http.get<{ messages: Array<ChatMessage & { starredAt: string; conversation: { id: string; type: string; name: string } }> }>(
      '/messaging/messages/starred'
    ).then(r => r.data),

  getPinnedMessages: (conversationId: string) =>
    http.get<{ messages: Array<ChatMessage & { pinnedAt: string; pinnedBy: { id: string; name: string } }> }>(
      `/messaging/conversations/${conversationId}/messages/pinned`
    ).then(r => r.data),

  getThreadReplies: (conversationId: string, messageId: string, params?: { beforeSequenceId?: number; limit?: number }) =>
    http.get<{
      rootMessage: ChatMessage;
      replies: ChatMessage[];
      hasMore: boolean;
    }>(`/messaging/conversations/${conversationId}/messages/${messageId}/thread`, { params })
      .then(r => r.data),

  getReactions: (conversationId: string, messageId: string) =>
    http.get<{ reactions: Record<string, Array<{ userId: string; name: string; avatar?: string }>> }>(
      `/messaging/conversations/${conversationId}/messages/${messageId}/reactions`
    ).then(r => r.data),

  // Channels
  getChannels: () =>
    http.get<{ channels: Channel[] }>('/messaging/channels')
      .then(r => r.data),

  createChannel: (data: {
    name: string;
    description?: string;
    category?: string;
    isPublic?: boolean;
  }) =>
    http.post<{ channel: Conversation }>('/messaging/channels', data)
      .then(r => r.data),

  joinChannel: (channelId: string) =>
    http.post<{ conversation: Conversation }>(`/messaging/channels/${channelId}/join`)
      .then(r => r.data),

  // Presence
  getPresence: (userIds: string[]) =>
    http.get<{ presence: Record<string, UserPresence> }>(
      '/messaging/presence',
      { params: { userIds: userIds.join(',') } }
    ).then(r => r.data),

  // Mark all conversations as read
  markAllAsRead: () =>
    http.post<{ success: boolean }>('/messaging/conversations/mark-all-read')
      .then(r => r.data),

  // Forward message
  forwardMessage: (params: { messageId: string; conversationIds: string[]; additionalMessage?: string }) =>
    http.post<{ forwarded: number }>('/messaging/messages/forward', {
      messageIds: [params.messageId],
      targetConversationIds: params.conversationIds,
      additionalMessage: params.additionalMessage,
    }).then(r => r.data),
};
